// src/pages/admin/reports/SessionsSummaryReport.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../../api';
import { listLevels } from '../../../api/levels';
import { listBatches } from '../../../api/batches';
import { regionalDateDaysAgo, todayRegionalDate, toISODateFromRegional } from '../../../utils/date';
import { buildSessionDateTimeParts } from '../../../utils/sessionDateTimeDisplay';
import BackButton from '../../../components/BackButton';
import DatePicker from '../../../components/common/DatePicker';
import ExcelDownloadButton from '../../../components/common/ExcelDownloadButton';
import PaginationFooter from '../../../components/common/PaginationFooter';
import SingleSelectSearch from '../../../components/common/SingleSelectSearch';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import UserLink from '../../../components/common/UserLink';
import useSortedClientPagination from '../../../hooks/useSortedClientPagination';
import { sessionsSummarySortColumns } from '../../../config/tableSortColumns';
import { levelDisplayName, localizeBatchName, userDisplayName } from '../../../utils/displayNames';
import { apiErrorMessage } from '../../../utils/apiError';

const levelOptionId = (l) => l?.levelId ?? l?.id ?? '';
const levelOptionName = (l, t) => l?.name || l?.levelName || levelDisplayName(null, levelOptionId(l), t);
const batchOptionId = (b) => b?.batchId ?? b?.id ?? '';
const batchOptionName = (b, t) => b?.name || b?.batchName || (batchOptionId(b) ? t('batches.batchNumber', { id: batchOptionId(b) }) : '');

const personLabel = (u, t) => {
    const name = [u.firstName, u.middleName, u.lastName].filter(Boolean).join(' ').trim();
    const id = u.id ?? u.userId;
    return name ? `${name} (#${id})` : (u.username ? `${u.username} (#${id})` : userDisplayName(null, id, t));
};

export default function SessionsSummaryReport({ embeddedInHub = false }) {
    const { t } = useTranslation();
    const [mode, setMode] = useState('student');
    const [personId, setPersonId] = useState('');
    const [levelId, setLevelId] = useState('');
    const [batchId, setBatchId] = useState('');
    const [from, setFrom] = useState(regionalDateDaysAgo(30));
    const [to, setTo] = useState(todayRegionalDate());
    const [students, setStudents] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [levels, setLevels] = useState([]);
    const [batches, setBatches] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [filtersLoadError, setFiltersLoadError] = useState(false);
    const [batchesLoadError, setBatchesLoadError] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        let cancelled = false;
        setFiltersLoading(true);
        setFiltersLoadError(false);
        (async () => {
            try {
                const [stuRes, coachRes, levelsRes] = await Promise.all([
                    API.get('/admin/users', { params: { role: 'STUDENT', page: 0, size: 1000 } }),
                    API.get('/admin/users', { params: { role: 'COACH', page: 0, size: 500 } }),
                    listLevels('ADMIN'),
                ]);
                if (cancelled) return;
                const stuRaw = stuRes?.data?.content ?? stuRes?.data ?? [];
                const coachRaw = coachRes?.data?.content ?? coachRes?.data ?? [];
                const levelsRaw = levelsRes?.data?.content ?? levelsRes?.data ?? [];
                setStudents(Array.isArray(stuRaw) ? stuRaw : []);
                setCoaches(Array.isArray(coachRaw) ? coachRaw : []);
                setLevels(Array.isArray(levelsRaw) ? levelsRaw : []);
                setFiltersLoadError(false);
            } catch (e) {
                console.error('Failed to load people/levels for Sessions Summary', e);
                if (!cancelled) {
                    setStudents([]);
                    setCoaches([]);
                    setLevels([]);
                    setFiltersLoadError(true);
                }
            } finally {
                if (!cancelled) setFiltersLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setBatchId('');
        setBatchesLoadError(false);
        (async () => {
            try {
                const params = levelId ? { levelId } : {};
                const res = await listBatches('ADMIN', params);
                if (cancelled) return;
                const raw = res?.data?.content ?? res?.data ?? [];
                setBatches(Array.isArray(raw) ? raw : []);
            } catch {
                if (!cancelled) {
                    setBatches([]);
                    setBatchesLoadError(true);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [levelId]);

    useEffect(() => {
        setPersonId('');
        setRows([]);
        setLoaded(false);
        setErr('');
    }, [mode]);

    const people = mode === 'student' ? students : coaches;

    const sortedLevels = useMemo(() => {
        return [...levels].sort((a, b) => levelOptionName(a, t).localeCompare(levelOptionName(b, t)));
    }, [levels]);

    const sortedBatches = useMemo(() => {
        return [...batches].sort((a, b) => batchOptionName(a, t).localeCompare(batchOptionName(b, t)));
    }, [batches]);

    const pagination = useSortedClientPagination(rows, {
        columns: sessionsSummarySortColumns,
        resetKey: `${mode}|${personId}|${levelId}|${batchId}|${from}|${to}|${rows.length}`,
        defaultSortKey: 'start',
        defaultSortDir: 'desc',
    });

    const load = async () => {
        if (!personId) {
            setErr(t('reports.common.selectPerson', { mode: mode === 'student' ? t('reports.common.student').toLowerCase() : t('reports.common.coach').toLowerCase() }));
            return;
        }
        const fromIso = toISODateFromRegional(from);
        const toIso = toISODateFromRegional(to);
        if (!fromIso || !toIso) {
            setErr(t('reports.common.selectValidDates'));
            return;
        }

        setLoading(true);
        setErr('');
        try {
            const params = {
                fromDate: fromIso,
                toDate: toIso,
            };
            if (mode === 'student') params.studentId = personId;
            else params.coachId = personId;
            if (levelId) params.levelId = Number(levelId);
            if (batchId) params.batchId = Number(batchId);

            const res = await API.get('/admin/reports/sessions-summary', { params });
            setRows(Array.isArray(res?.data) ? res.data : []);
            setLoaded(true);
        } catch (e) {
            setRows([]);
            setLoaded(true);
            setErr(apiErrorMessage(e, t('reports.sessionsSummary.loadFailed')));
        } finally {
            setLoading(false);
        }
    };

    const fmtWhen = (r) => {
        const parts = buildSessionDateTimeParts(r.startTime, r.endTime);
        if (parts?.date && parts?.timeRange) return `${parts.date} ${parts.timeRange}`;
        if (r.startTime) {
            const single = buildSessionDateTimeParts(r.startTime, r.startTime);
            if (single?.date) return `${single.date}${single.timeRange ? ` ${single.timeRange}` : ''}`;
        }
        return '—';
    };

    const sortedPeople = useMemo(() => {
        return [...people].sort((a, b) => personLabel(a, t).localeCompare(personLabel(b, t)));
    }, [people]);

    const personOptions = useMemo(
        () => sortedPeople.map((p) => {
            const id = p.id ?? p.userId;
            return { value: String(id), label: personLabel(p, t) };
        }),
        [sortedPeople]
    );

    const levelOptions = useMemo(
        () => sortedLevels.map((l) => ({
            value: String(levelOptionId(l)),
            label: levelOptionName(l, t),
        })),
        [sortedLevels]
    );

    const batchOptions = useMemo(
        () => sortedBatches.map((b) => ({
            value: String(batchOptionId(b)),
            label: batchOptionName(b, t),
        })),
        [sortedBatches]
    );

    return (
        <div className={embeddedInHub ? "space-y-4 min-w-0 px-3 sm:px-6 pb-6" : "p-3 sm:p-6 space-y-4 min-w-0"}>
            {!embeddedInHub && <BackButton />}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    {!embeddedInHub && (
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{t('reports.sessionsSummary.title')}</h1>
                    )}
                    <p className={`text-sm text-gray-600 ${embeddedInHub ? '' : 'mt-1'}`}>
                        {t('reports.sessionsSummary.subtitle')}
                    </p>
                </div>
                <ExcelDownloadButton
                    columns={[
                        { header: t('reports.common.sessionDateTime'), key: '_when', render: (_, r) => fmtWhen(r), width: 28 },
                        { header: t('reports.common.level'), key: 'levelName', render: (v) => localizeBatchName(v, t) || '—', width: 18 },
                        { header: t('reports.common.batch'), key: 'batchName', render: (v) => localizeBatchName(v, t) || '—', width: 22 },
                        { header: t('reports.common.coach'), key: 'coachName', render: (v) => localizeBatchName(v, t) || '—', width: 22 },
                        { header: t('reports.common.sessionTopic'), key: 'sessionTopic', render: (v) => localizeBatchName(v, t) || '—', width: 40 },
                    ]}
                    data={rows}
                    filename="sessions-summary"
                    disabled={!loaded || rows.length === 0}
                />
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 space-y-4">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setMode('student')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            mode === 'student'
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {t('reports.common.byStudent')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('coach')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            mode === 'coach'
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        {t('reports.common.byCoach')}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {mode === 'student' ? t('reports.common.student') : t('reports.common.coach')}
                        </label>
                        <SingleSelectSearch
                            options={personOptions}
                            value={personId}
                            onChange={setPersonId}
                            loading={filtersLoading}
                            loadError={filtersLoadError}
                            placeholder={t('reports.common.choosePerson', {
                                mode: mode === 'student' ? t('reports.common.student').toLowerCase() : t('reports.common.coach').toLowerCase(),
                            })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.common.level')}</label>
                        <SingleSelectSearch
                            options={levelOptions}
                            value={levelId}
                            onChange={setLevelId}
                            loading={filtersLoading}
                            loadError={filtersLoadError}
                            emptyLabel={t('reports.common.allLevelsLabel')}
                            placeholder={t('reports.common.allLevelsLabel')}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.common.batch')}</label>
                        <SingleSelectSearch
                            options={batchOptions}
                            value={batchId}
                            onChange={setBatchId}
                            loadError={batchesLoadError}
                            emptyLabel={t('reports.common.allBatchesLabel')}
                            placeholder={t('reports.common.allBatchesLabel')}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.common.fromDate')}</label>
                        <DatePicker
                            value={from}
                            onChange={setFrom}
                            className="border border-gray-300 p-2 rounded-md w-full"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.common.toDate')}</label>
                        <DatePicker
                            value={to}
                            onChange={setTo}
                            className="border border-gray-300 p-2 rounded-md w-full"
                        />
                    </div>
                </div>

                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="btn-primary px-6 py-2 disabled:opacity-50"
                >
                    {loading ? t('common.loading') : t('reports.common.generateReport')}
                </button>
            </div>

            {err && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">{err}</div>
            )}

            {loaded && !loading && !err && (
                <>
                    <div className="md:hidden space-y-3">
                        {pagination.pageItems.map((r, i) => (
                            <article
                                key={String(r.sessionId ?? i)}
                                className="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm"
                            >
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                    {t('reports.common.sessionDateTime')}
                                </p>
                                <p className="text-sm font-semibold text-gray-900 tabular-nums mb-3">
                                    {fmtWhen(r)}
                                </p>
                                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm border-t border-gray-100 pt-3">
                                    <div className="min-w-0">
                                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.level')}</dt>
                                        <dd className="font-medium text-gray-800 truncate">{r.levelName || '—'}</dd>
                                    </div>
                                    <div className="min-w-0">
                                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.batch')}</dt>
                                        <dd className="font-medium text-gray-800 truncate">{localizeBatchName(r.batchName, t) || '—'}</dd>
                                    </div>
                                    <div className="min-w-0 col-span-2">
                                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.coach')}</dt>
                                        <dd className="font-medium text-gray-800">
                                            {r.coachId && r.coachName ? (
                                                <UserLink id={r.coachId} name={r.coachName} />
                                            ) : (
                                                r.coachName || '—'
                                            )}
                                        </dd>
                                    </div>
                                    <div className="min-w-0 col-span-2">
                                        <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t('reports.common.sessionTopic')}</dt>
                                        <dd className="font-medium text-gray-800 whitespace-pre-wrap break-words">
                                            {r.sessionTopic?.trim() || '—'}
                                        </dd>
                                    </div>
                                </dl>
                            </article>
                        ))}
                        {rows.length === 0 && (
                            <div className="rounded-xl border bg-white p-6 text-center text-gray-500">
                                {t('reports.common.noSessionsFilter')}
                            </div>
                        )}
                    </div>

                    <div className="hidden md:block bg-white rounded-lg shadow-sm border overflow-hidden">
                        <div className="overflow-x-auto responsive-table-scroll">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <SortableTableHeader
                                            label={t('reports.common.sessionDateTime')}
                                            columnKey="start"
                                            sortKey={pagination.sortKey}
                                            sortDir={pagination.sortDir}
                                            onSort={pagination.toggleSort}
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                        />
                                        <SortableTableHeader
                                            label={t('reports.common.level')}
                                            columnKey="level"
                                            sortKey={pagination.sortKey}
                                            sortDir={pagination.sortDir}
                                            onSort={pagination.toggleSort}
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                        />
                                        <SortableTableHeader
                                            label={t('reports.common.batch')}
                                            columnKey="batch"
                                            sortKey={pagination.sortKey}
                                            sortDir={pagination.sortDir}
                                            onSort={pagination.toggleSort}
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                        />
                                        <SortableTableHeader
                                            label={t('reports.common.coach')}
                                            columnKey="coach"
                                            sortKey={pagination.sortKey}
                                            sortDir={pagination.sortDir}
                                            onSort={pagination.toggleSort}
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                        />
                                        <SortableTableHeader
                                            label={t('reports.common.sessionTopic')}
                                            columnKey="topic"
                                            sortKey={pagination.sortKey}
                                            sortDir={pagination.sortDir}
                                            onSort={pagination.toggleSort}
                                            className="px-4 py-3 text-left font-semibold text-gray-700"
                                        />
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagination.pageItems.map((r, i) => (
                                        <tr key={String(r.sessionId ?? i)} className="border-t hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap tabular-nums">{fmtWhen(r)}</td>
                                            <td className="px-4 py-3">{r.levelName || '—'}</td>
                                            <td className="px-4 py-3">{localizeBatchName(r.batchName, t) || '—'}</td>
                                            <td className="px-4 py-3">
                                                {r.coachId && r.coachName ? (
                                                    <UserLink id={r.coachId} name={r.coachName} />
                                                ) : (
                                                    r.coachName || '—'
                                                )}
                                            </td>
                                            <td className="px-4 py-3 max-w-xl whitespace-pre-wrap break-words">
                                                {r.sessionTopic?.trim() || <span className="text-gray-400">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                    {rows.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                                {t('reports.common.noSessionsFilter')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {pagination.total > 0 && (
                        <div className="bg-white rounded-lg border md:border-0 md:rounded-none overflow-hidden">
                            <PaginationFooter
                                page={pagination.page}
                                size={pagination.size}
                                total={pagination.total}
                                onPageChange={pagination.setPage}
                                onSizeChange={pagination.setSize}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
